if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
        }
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;
  
        // Update thumbnails immediately on slide change (remove debounce for instant UI feedback)
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));

        // Also update thumbnails immediately when nav buttons are clicked, without waiting for scroll/update
        const prevBtn = this.elements.viewer.querySelector('button[name="previous"]');
        const nextBtn = this.elements.viewer.querySelector('button[name="next"]');
        if (prevBtn) prevBtn.addEventListener('click', this.onViewerNavClick.bind(this));
        if (nextBtn) nextBtn.addEventListener('click', this.onViewerNavClick.bind(this));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();

        if (this.dataset.setHeight) {
          window.setTimeout(this.setHeight.bind(this));
          this.resizeObserver = new ResizeObserver(this.setHeight.bind(this));
          this.resizeObserver.observe(this.elements.viewer);
          this.bindImageHeightUpdates();
        }
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.unbindImageHeightUpdates();
      }
  
      onSlideChanged(event) {
        const currentElement = event && event.detail && event.detail.currentElement;
        if (!currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ currentElement.dataset.mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
      }

      onViewerNavClick(event) {
        const viewer = this.elements.viewer;
        if (!viewer || !viewer.sliderItemsToShow || viewer.sliderItemsToShow.length === 0) return;

        const total = viewer.sliderItemsToShow.length;
        const step = parseInt(event.currentTarget.dataset.step || '1', 10);
        const goingNext = event.currentTarget.name === 'next';

        let current = viewer.currentPage || 1;
        let target = goingNext ? current + step : current - step;

        // Wrap if slideshow loops
        if (target > total) target = 1;
        if (target < 1) target = total;

        const targetElement = viewer.sliderItemsToShow[target - 1];
        if (!targetElement) return;

        const mediaId = targetElement.dataset.mediaId || (targetElement.querySelector('[data-media-id]') && targetElement.querySelector('[data-media-id]').dataset.mediaId);
        if (!mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setHeight() {
        if (!this.elements.viewer) return;

        const slides = this.elements.viewer.querySelectorAll('.product__media-item');
        let measuredHeight = 0;

        slides.forEach((slide) => {
          const image = this.getSlideImage(slide);
          const media = this.getSlideMedia(slide);
          const slideHeight =
            this.getImageRenderedHeight(image, media) ||
            this.getElementHeight(image) ||
            this.getElementHeight(media) ||
            this.getElementHeight(slide);

          if (slideHeight > measuredHeight) measuredHeight = slideHeight;
        });

        if (!measuredHeight) measuredHeight = this.elements.viewer.offsetHeight;
        if (!measuredHeight) return;

        this.style.setProperty('--height', `${measuredHeight}px`);
      }

      bindImageHeightUpdates() {
        this.unbindImageHeightUpdates();
        this.viewerImages = [...this.elements.viewer.querySelectorAll('.product__modal-opener--image img, .product__media img, .deferred-media__poster img')];
        this.imageLoadHandler = () => this.setHeight();

        this.viewerImages.forEach((image) => {
          image.addEventListener('load', this.imageLoadHandler);
          if (image.complete) this.setHeight();
        });
      }

      unbindImageHeightUpdates() {
        if (!this.imageLoadHandler || !this.viewerImages) return;

        this.viewerImages.forEach((image) => {
          image.removeEventListener('load', this.imageLoadHandler);
        });
      }

      getSlideMedia(slide) {
        if (!slide) return null;
        return slide.querySelector('.product__media.media, .deferred-media.media, product-model.media');
      }

      getSlideImage(slide) {
        if (!slide) return null;
        return slide.querySelector('.product__modal-opener--image img, .product__media img, .deferred-media__poster img');
      }

      getElementHeight(element) {
        if (!element) return 0;
        const rectHeight = element.getBoundingClientRect().height;
        if (rectHeight) return Math.round(rectHeight);
        return element.offsetHeight || 0;
      }

      getImageRenderedHeight(imageElement, mediaElement) {
        if (!imageElement) return 0;

        const width =
          (mediaElement && (Math.round(mediaElement.getBoundingClientRect().width) || mediaElement.clientWidth)) ||
          Math.round(imageElement.getBoundingClientRect().width) ||
          imageElement.clientWidth ||
          0;
        if (!width) return 0;

        if (imageElement.naturalWidth && imageElement.naturalHeight) {
          return Math.round(width * (imageElement.naturalHeight / imageElement.naturalWidth));
        }

        if (!mediaElement) return 0;

        const paddingTop = parseFloat(mediaElement.style.paddingTop || '');
        if (!Number.isFinite(paddingTop)) return 0;
        return Math.round((paddingTop / 100) * width);
      }
  
      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
  
        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }
  
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({behavior: 'smooth'});
          }
        });
        this.playActiveMedia(activeMedia);
  
        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
  
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
  
        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const isVertical = this.elements.thumbnails.offsetHeight > this.elements.thumbnails.offsetWidth;

        // Determine orientation by comparing the width and height of the slider container
      
        // Scroll to the thumbnail depending on orientation
        const previousThumbnail = thumbnail.previousElementSibling;
        if(previousThumbnail) {

        const scrollOptions = isVertical
          ? { top: (previousThumbnail || thumbnail).offsetTop - 2, behavior: 'smooth' }
          : { left: thumbnail.offsetLeft, behavior: 'smooth' };
        

      
          this.elements.thumbnails.slider.scrollTo(scrollOptions);
        }
      }
  
      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }
  
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }
  
      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
  
      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
      }
    });
  }